import React, { Component, ReactNode } from "react";
import { classNames } from "primereact/utils";

/* ------------------------------------------------------------------ */
/*  AppBadge – semantic status badge
/*  Usage:  <AppBadge variant="success">Active</AppBadge>
/* ------------------------------------------------------------------ */

type BadgeVariant = "success" | "danger" | "warning" | "info" | "default" | "primary" | "orange" | "neutral";
type BadgeStyle = "filled" | "bordered";

interface AppBadgeProps {
    children?: ReactNode;
    variant?: BadgeVariant;
    badgeStyle?: BadgeStyle;
    className?: string;
    /** Render as a small dot indicator instead of text badge */
    dot?: boolean;
}

const variantClasses: Record<BadgeVariant, Record<BadgeStyle, string>> = {
    success: {
        filled: "bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400",
        bordered: "border border-success-300 dark:border-success-700 text-success-700 dark:text-success-400 bg-success-50/50 dark:bg-success-900/10",
    },
    danger: {
        filled: "bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-400",
        bordered: "border border-danger-300 dark:border-danger-700 text-danger-700 dark:text-danger-400 bg-danger-50/50 dark:bg-danger-900/10",
    },
    warning: {
        filled: "bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-400",
        bordered: "border border-warning-300 dark:border-warning-700 text-warning-700 dark:text-warning-400 bg-warning-50/50 dark:bg-warning-900/10",
    },
    info: {
        filled: "bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-400",
        bordered: "border border-info-300 dark:border-info-700 text-info-700 dark:text-info-400 bg-info-50/50 dark:bg-info-900/10",
    },
    primary: {
        filled: "bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400",
        bordered: "border border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-400 bg-primary-50/50 dark:bg-primary-900/10",
    },
    orange: {
        filled: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
        bordered: "border border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-400 bg-orange-50/50 dark:bg-orange-900/10",
    },
    neutral: {
        filled: "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300",
        bordered: "border border-neutral-300 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 bg-neutral-50/50 dark:bg-neutral-800/50",
    },
    default: {
        filled: "bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-300",
        bordered: "border border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 bg-neutral-50/50 dark:bg-neutral-800/50",
    },
};

const dotColorClasses: Record<BadgeVariant, string> = {
    success: "bg-emerald-500",
    danger: "bg-red-500",
    warning: "bg-amber-500",
    info: "bg-blue-500",
    primary: "bg-primary-500",
    orange: "bg-orange-500",
    neutral: "bg-neutral-400",
    default: "bg-neutral-400",
};

class AppBadge extends Component<AppBadgeProps> {
    render() {
        const { children, badgeStyle = "filled", className, dot } = this.props;

        // Callers frequently pass a variant computed from API data (and cast with `as any`),
        // so an unrecognised value must not break the render.
        const variant: BadgeVariant = this.props.variant && this.props.variant in variantClasses
            ? this.props.variant
            : "default";

        if (dot) {
            return (
                <span className={classNames("inline-flex items-center gap-1.5", className)}>
                    <span className={classNames("w-1.5 h-1.5 rounded-full", dotColorClasses[variant])} />
                    <span className="text-sm text-neutral-700 dark:text-neutral-300">{children}</span>
                </span>
            );
        }

        return (
            <span
                className={classNames(
                    "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                    variantClasses[variant][badgeStyle],
                    className,
                )}
            >
                {children}
            </span>
        );
    }
}

export default AppBadge;

export function StatusBadge(active: boolean, trueLabel: string, falseLabel?: string) {
    return (
        <AppBadge variant={active ? "success" : "danger"}>
            {active ? trueLabel : (falseLabel ?? trueLabel)}
        </AppBadge>
    );
}
